import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, forkJoin } from 'rxjs';
import { Constant, LegDetailStatus, RequestFor, RoleName, TransportType } from 'src/app/@shared/constants/Constant';
import { IGetLegDetailsPayLoad, ILegDetailsViewModel } from 'src/app/model/Vendor.model';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import { ILegDetailsConfig } from 'src/app/model/reusableConfigData.model';
import { UpdateStatusModel } from 'src/app/service/driver.model';
import { ItineraryService } from 'src/app/service/itinerary.service';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { VendorDashboardService } from 'src/app/service/vendor-dashboard.service';
import { EventConstant } from 'src/app/sse/event.constant';
import { SignalrService } from 'src/app/sse/signalr.service';
import { LegDetailService } from './leg-details.service';

declare var $: any;

@Component({
  selector: 'app-leg-details',
  templateUrl: './leg-details.component.html',
  styleUrls: ['./leg-details.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegDetailsComponent implements OnInit, OnDestroy {
  @Input('legDetailsConfig') legDetailsConfig: ILegDetailsConfig;
  @Output('legDetailsLength') legDetailsLength = new EventEmitter();
  @Output('legListEmitter') legListEmitter = new EventEmitter();
  @Output('emitLegDetailForMap') emitLegDetailForMap = new EventEmitter();
  private _subscriptions: Subscription = new Subscription();

  public user: ILoggedInUserViewModel;
  public legDetails: ILegDetailsViewModel[] = [];
  public vehicleStatusList: any[] = [];
  public airCraftStatusList: any[] = [];
  public requestFor = RequestFor;
  public timeZone: any;
  public transportType = TransportType;
  public detailLinkName: string = '';
  public loader: boolean = false;
  public isNotesModelOpen: boolean = false;
  public isBadgeVisible: boolean = false;
  public requestIDForNotes: string;
  public legDetailStatus = LegDetailStatus;
  public forPage: string;
  public isAdminRole: boolean = false;
  public isCustomer: boolean;
  public isInternalReadOnly: boolean = false;
  public isManagementRole: boolean = false;

  constructor(
    private _vendorDashboardService: VendorDashboardService,
    private _cdr: ChangeDetectorRef,
    private _router: Router,
    private _itineraryService: ItineraryService,
    private _serviceRequestService: ServiceRequestService,
    public legDetailService: LegDetailService,
    private _signalRService: SignalrService,
    private _toast: ToastrService,
  ) {
    this._subscriptions.add(
      this._serviceRequestService.timZoneChange.subscribe((value) => {
        this.timeZone = Constant.selectedTimeZoneShortValue;
        this._cdr.detectChanges();
      }),
    );
    this.detailLinkName = _router.url.includes('v2/customer-dashboard') ? 'More detail' : 'Service Request Details';
    this._subscriptions.add(
      this._itineraryService.$refreshLegDetails.subscribe(() => {
        if (this.legDetailService.dialogRef) {
          this.legDetailService.dialogRef.close();
        }
        this.getDetailsAccordingToConfig();
      }),
    );
    if (this._router.url.includes('common-itinerary')) {
      this.forPage = 'common-itinerary';
    }
  }

  ngOnInit() {
    this.loader = true;
    this.user = JSON.parse(localStorage.getItem('user'));
    this.isInternalReadOnly = this.user.role === RoleName.InternalReadOnly;
    this.isManagementRole = this.user.role == RoleName.Management;
    if (this.user != null) {
      this.isAdminRole =
        this.user.role == RoleName.ExecutiveAdmin || this.user.role == RoleName.Coordinator || this.user.role == RoleName.Management;
      this.isCustomer = this.user.role == RoleName.CustomerAdmin || this.user.role == RoleName.CustomerCoordinator;
      this.subscribeAssignmentRequestEventListener();
      this.subscribeChangeLegStatusEventListener();
    } else {
      this.setOutSideOrgUser();
    }
    this.getDetailsAccordingToConfig();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  setOutSideOrgUser() {
    this.user = {
      businessUnitId: '',
      businessUnitList: [],
      memberId: '',
      name: '',
      firstName: '',
      middleName: '',
      lastName: '',
      emailID: '',
      memberType: '',
      role: 'OutSideOrg',
      roleId: '',
      phoneNumber: '',
      zipcode: '',
      cityId: '',
      city: '',
      stateId: '',
      state: '',
      streetAddress: '',
      description: '',
      dateOfBirth: null,
      height: null,
      weight: null,
      organizationType: '',
      contactId: '',
      addressId: '',
      organizationTypeId: '',
      profile: null,
      files: null,
      userName: '',
      createdOn: null,
      addressLocationType: '',
      isGlobal: null,
      assignedRoleList: [],
    };
  }

  subscribeAssignmentRequestEventListener() {
    this._signalRService.sseEventListener(EventConstant.vendorAssignmentRequestEvent);
    this._subscriptions.add(
      this._signalRService.eventDataChange.subscribe(
        (value) => {
          if (value.eventName == EventConstant.vendorAssignmentRequestEvent && value.data == this.user.businessUnitId) {
            this.getDetailsAccordingToConfig();
          }
        },
        (error) => {
          console.log(error);
        },
      ),
    );
  }

  subscribeChangeLegStatusEventListener() {
    this._signalRService.sseEventListener(EventConstant.changeLegStatusEvent);
    this._subscriptions.add(
      this._signalRService.eventDataChange.subscribe(
        (value) => {
          if (value.eventName == EventConstant.changeLegStatusEvent) {
            let eventData = value.data;
            let updatedLeg = this.legDetails.find((leg) => leg.legId === eventData.legId);
            if (updatedLeg != undefined) {
              updatedLeg.status = eventData.legStatus;
              this._cdr.detectChanges();
            }
          }
        },
        (error) => {
          console.log(error);
        },
      ),
    );
  }

  getDetailsAccordingToConfig(config?: ILegDetailsConfig) {
    if (config) {
      this.legDetailsConfig = config;
    }
    if (this.legDetailsConfig && this.legDetailsConfig.requestFor == RequestFor.LegAssignmentRequests) {
      this.getLegAssignmentRequestList(this.legDetailsConfig.payLoadData.vendorId);
    }
    if (
      this.legDetailsConfig &&
      (this.legDetailsConfig.requestFor == null || this.legDetailsConfig.requestFor == RequestFor.ActiveRequests)
    ) {
      this.getLegDetailsList(this.legDetailsConfig.payLoadData);
    }
    if (this.legDetailsConfig.isGetLegStatusList) {
      this.getGroundAndAirStatusList();
    }
  }

  getLegAssignmentRequestList(vendorId: string) {
    this._subscriptions.add(
      this._vendorDashboardService.getLegAssignmentRequestById(vendorId).subscribe((response) => {
        this.legDetails = response;
        const legDetailsLength = {
          legAssignmentRequestListLength: this.legDetails.length,
        };
        this.legDetailsLength.emit(legDetailsLength);
        this.loader = false;
        this._cdr.detectChanges();
      }),
    );
  }

  getLegDetailsList(payLoad: IGetLegDetailsPayLoad) {
    this.loader = true;
    this._cdr.detectChanges();
    let legDetailsLength = {
      activeRequestListLength: 0,
    };
    this._subscriptions.add(
      this._vendorDashboardService.getLegDetailsList(payLoad).subscribe(
        (response) => {
          this.legDetails = response;
          legDetailsLength.activeRequestListLength = this.legDetails.length;
          this.legDetailsLength.emit(legDetailsLength);
          this.legListEmitter.emit(this.legDetails);
          this.loader = false;
          this._cdr.detectChanges();
        },
        (error) => {
          console.log(error);
          this.loader = false;
          this.legDetailsLength.emit(legDetailsLength);
          this._cdr.detectChanges();
        },
      ),
    );
  }

  getGroundAndAirStatusList() {
    const $groundStatusListObs = this._itineraryService.vehicleStatusCategory();
    const $airStatusListObs = this._itineraryService.aircraftStatusCategory();

    this._subscriptions.add(
      forkJoin({ groundStatusList: $groundStatusListObs, airStatusList: $airStatusListObs }).subscribe((response) => {
        this.vehicleStatusList = response.groundStatusList;
        this.airCraftStatusList = response.airStatusList;
        this._cdr.detectChanges();
      }),
    );
  }

  updateLegStatus(legId, currentStatus) {
    const updateStatusPayload: UpdateStatusModel = {
      LegId: legId,
      Status: currentStatus,
      updatedBy: this.legDetailsConfig.loggedInUserId,
    };

    this._subscriptions.add(
      this._itineraryService.updateDriverStatus(updateStatusPayload, false).subscribe(
        () => {
          this.getLegDetailsList(this.legDetailsConfig.payLoadData);
        },
        (error) => console.log('update error', error),
      ),
    );
  }

  navigateToItinerarypage(serviceRequestId: string) {
    this._router.navigate([`/common-itinerary/${serviceRequestId}/VendorDashboard`]);
    this._vendorDashboardService.closeLegDetailsPopUp.next(true); //This is used to close leg details popup while opening from vendor dashboard.
  }

  openAddNotePopup(requestId, isBadgeVisible) {
    this.requestIDForNotes = requestId;
    this.isBadgeVisible = isBadgeVisible;
    this.isNotesModelOpen = true;
  }

  closeNotesModel() {
    this.isNotesModelOpen = false;
  }

  emitLegDetail(legDetail: ILegDetailsViewModel) {
    this.emitLegDetailForMap.emit(legDetail);
  }

  toggleSelection(legDetail: ILegDetailsViewModel, checkboxRef: HTMLInputElement) {
    if (
      this.legDetailService.selectedAssignmentRequestList.every((x) => x.tripId !== legDetail.tripId) &&
      this.legDetailService.selectedAssignmentRequestList.length > 0
    ) {
      this._toast.warning('Please Select the Same Trips.', 'Warning', { timeOut: 6000, positionClass: 'toast-top-center' });
      checkboxRef.checked = false;
    } else {
      this.legDetailService.manageSelectedLegDetailList(legDetail);
    }
  }

  togglePopOver() {
    $(document).ready(function () {
      $('[data-toggle="popover"]').popover();
    });
  }
}
